/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, Activity } from 'lucide-react';

/**
 * DepartmentGantt — studio-grade, per-department timeline.
 *
 * Rows:  SCRIPT, STORYBOARD, ANIMATIC, DESIGN (CHAR/BG), MODELING,
 *        TEXTURING, RIGGING, LAYOUT, ANIMATION, LIGHTING, RENDERING,
 *        COMP & EDIT, SOUND & MUSIC, FINAL DELIVERY
 *
 * Per-row bar = min(task.start) → max(task.end) for that department, with
 * the bar filled proportional to average % progress and a stripe pattern
 * showing "Not Started / In Progress / Review / Done" counts.
 *
 * Dependencies are drawn as thin connector lines between logically-chained
 * departments (Storyboard → Animatic → Layout → Animation,
 * Modeling → Rigging → Animation, Animation → Lighting → Rendering → Comp).
 */
const DEPARTMENTS = [
  { key: 'script',     label: 'Script',                 owner: 'Client',             color: '#60a5fa', match: ['script'] },
  { key: 'storyboard', label: 'Storyboard / Animatics', owner: 'Storyboard Artist',  color: '#a78bfa', match: ['storyboard','board','animatic'] },
  { key: 'concept',    label: 'Concept Art',            owner: 'Producer → Client',  color: '#f472b6', match: ['concept','design'] },
  { key: 'modeling',   label: 'Asset Build',            owner: 'Modelers (per asset)', color: '#10b981', match: ['model','modeling','textur','shade','shading','asset_build'] },
  { key: 'rigging',    label: 'Rigging',                owner: 'Rigger',             color: '#14b8a6', match: ['rig'] },
  { key: 'animation',  label: 'Layout & Animation',     owner: 'Animators (per shot)', color: '#f59e0b', match: ['anim','animation','layout'] },
  { key: 'lighting',   label: 'Lighting',               owner: 'Lighter (per shot)', color: '#fb923c', match: ['light','lighting'] },
  { key: 'rendering',  label: 'Rendering',              owner: 'Lighter',            color: '#ec4899', match: ['render','rendering'] },
  { key: 'comp',       label: 'Comp & Edit',            owner: 'Comper (per shot)',  color: '#84cc16', match: ['comp','edit','editing','composit'] },
  { key: 'sound',      label: 'Sound & Music',          owner: 'Producer',           color: '#eab308', match: ['sound','music','audio','vo'] },
  { key: 'delivery',   label: 'Final Delivery',         owner: 'Producer',           color: '#ef4444', match: ['deliver','final','master'] },
];

// Dependency chain — used to draw connector arrows (from → to by key)
const DEPENDENCIES = [
  ['script','storyboard'], ['storyboard','animation'],
  ['concept','modeling'],  ['modeling','rigging'],    ['rigging','animation'],
  ['animation','lighting'],['lighting','rendering'],
  ['rendering','comp'],    ['comp','delivery'],       ['sound','delivery'],
];

function matchDept(stage) {
  const s = (stage || '').toLowerCase();
  return DEPARTMENTS.find(d => d.match.some(m => s.includes(m)));
}

export default function DepartmentGantt({ tasks = [], project = null }) {
  const { rows, weeks, todayWeek } = useMemo(() => {
    // Compute timeline from tasks OR project start/end
    const starts = [], ends = [];
    tasks.forEach(t => {
      const s = t.assigned_at || t.created_at || t.start_date;
      const e = t.deadline || t.due_at || t.end_date;
      if (s) starts.push(new Date(s).getTime());
      if (e) ends.push(new Date(e).getTime());
    });
    if (project?.start_date) starts.push(new Date(project.start_date).getTime());
    if (project?.end_date)   ends.push(new Date(project.end_date).getTime());

    let minT, maxT;
    if (starts.length && ends.length) {
      minT = Math.min(...starts);
      maxT = Math.max(...ends, minT + 7*86400000);
    } else {
      // Fallback window: last 2 weeks → +10 weeks for visual purposes
      const now = Date.now();
      minT = now - 14 * 86400000;
      maxT = now + 10 * 7 * 86400000;
    }
    // Snap to week boundaries
    const start = new Date(minT);
    start.setHours(0,0,0,0);
    while (start.getDay() !== 1) start.setDate(start.getDate() - 1); // Monday
    const end = new Date(maxT);
    while (end.getDay() !== 0) end.setDate(end.getDate() + 1);
    const totalMs = Math.max(1, end.getTime() - start.getTime());
    const weekMs = 7 * 86400000;
    const weekCount = Math.max(8, Math.ceil(totalMs / weekMs));
    const weeks = Array.from({ length: weekCount }, (_, i) => {
      const d = new Date(start.getTime() + i * weekMs);
      return {
        idx: i + 1,
        label: `W${i + 1}`,
        date: d.toLocaleDateString('default', { month: 'short', day: '2-digit' }),
      };
    });

    const todayWeek = Math.max(0, Math.min(100, ((Date.now() - start.getTime()) / totalMs) * 100));

    // Build per-department aggregate
    const bucket = {};
    DEPARTMENTS.forEach(d => { bucket[d.key] = { ...d, tasks: [], starts: [], ends: [], progressSum: 0, statusCounts: { not_started:0, in_progress:0, review:0, done:0 } }; });

    tasks.forEach(t => {
      const dept = matchDept(t.task_type || t.type || t.stage);
      if (!dept) return;
      const b = bucket[dept.key];
      b.tasks.push(t);
      const s = t.assigned_at || t.created_at || t.start_date;
      const e = t.deadline || t.due_at || t.end_date;
      if (s) b.starts.push(new Date(s).getTime());
      if (e) b.ends.push(new Date(e).getTime());
      b.progressSum += (t.progress || 0);
      const st = (t.status || '').toLowerCase();
      if (st === 'completed' || st === 'approved' || st === 'done') b.statusCounts.done++;
      else if (st === 'in_progress' || st === 'in-progress') b.statusCounts.in_progress++;
      else if (st === 'review' || st === 'submitted') b.statusCounts.review++;
      else b.statusCounts.not_started++;
    });

    const rows = DEPARTMENTS.map(d => {
      const b = bucket[d.key];
      if (b.tasks.length === 0) {
        return { ...d, empty: true, count: 0 };
      }
      const bStart = Math.min(...b.starts, ...b.ends) || start.getTime();
      const bEnd   = Math.max(...b.ends, ...b.starts) || bStart + weekMs;
      const left = ((bStart - start.getTime()) / totalMs) * 100;
      const width = Math.max(1.5, ((bEnd - bStart) / totalMs) * 100);
      const avgProgress = Math.round(b.progressSum / Math.max(1, b.tasks.length));
      return {
        ...d,
        left, width,
        count: b.tasks.length,
        avgProgress,
        statusCounts: b.statusCounts,
      };
    });

    return { rows, weeks, todayWeek };
  }, [tasks, project]);

  const rowH = 28;
  const rowsWithData = rows.filter(r => !r.empty);
  const totalTasks = rowsWithData.reduce((s,r) => s + (r.count||0), 0);

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5" data-testid="department-gantt">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-bold text-white">
            <Calendar className="h-4 w-4 text-violet-400"/>
            Production Timeline · Department View
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Live roll-up across {rowsWithData.length}/{DEPARTMENTS.length} departments · {totalTasks} active task{totalTasks!==1?'s':''} · auto-updates when artists log progress
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Legend/>
          <Link to="/gantt" className="text-[11px] text-violet-300 hover:text-violet-200 inline-flex items-center gap-1">
            Shot-level Gantt <ChevronRight className="h-3 w-3"/>
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="relative" style={{ minWidth: 900 }}>
          {/* Week header */}
          <div className="flex items-end gap-0 border-b border-zinc-800 pb-2 sticky top-0 bg-zinc-900/60">
            <div className="w-36 shrink-0"/>
            <div className="flex-1 relative">
              <div className="flex">
                {weeks.map((w, i) => (
                  <div key={i} className="flex-1 text-center border-r border-zinc-800/70 pr-1" data-testid={`week-${i+1}`}>
                    <div className="text-[10px] font-bold text-zinc-300">{w.label}</div>
                    <div className="text-[9px] text-zinc-600 font-mono">{w.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className="relative">
            {/* TODAY marker */}
            <div className="absolute top-0 bottom-0 w-px bg-violet-500/80 z-20 pointer-events-none"
                 style={{ left: `calc(9rem + ${todayWeek}% * (100% - 9rem) / 100)` }}>
              <div className="absolute -top-0.5 -translate-x-1/2 bg-violet-500 text-white text-[9px] font-bold px-1 rounded-sm">TODAY</div>
            </div>

            {rows.map((r, i) => (
              <GanttRow key={r.key} row={r} rowH={rowH} index={i} />
            ))}

            {/* Dependency arrows */}
            <svg className="absolute inset-0 pointer-events-none" style={{ left: '9rem', top: 0 }} width="100%" height={rows.length * rowH}>
              {DEPENDENCIES.map(([fromKey, toKey], i) => {
                const from = rows.find(r => r.key === fromKey);
                const to   = rows.find(r => r.key === toKey);
                if (!from || !to || from.empty || to.empty) return null;
                const fromIdx = DEPARTMENTS.findIndex(d => d.key === fromKey);
                const toIdx   = DEPARTMENTS.findIndex(d => d.key === toKey);
                const y1 = fromIdx * rowH + rowH/2;
                const y2 = toIdx   * rowH + rowH/2;
                const x1 = `${(from.left ?? 0) + (from.width ?? 0)}%`;
                const x2 = `${to.left ?? 0}%`;
                return (
                  <g key={i} opacity="0.35">
                    <path d={`M ${x1} ${y1} L ${x2} ${y2}`} stroke="#71717a" strokeWidth="1" fill="none" strokeDasharray="3 3" />
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Bottom legend */}
          {totalTasks === 0 && (
            <div className="py-10 text-center text-xs text-zinc-500">
              <Activity className="h-6 w-6 text-zinc-700 mx-auto mb-2"/>
              No tasks scheduled yet. As soon as producers allot work across departments, this chart populates live.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───── single row ───── */
function GanttRow({ row, rowH, index }) {
  const testid = `gantt-row-${row.key}`;
  const progress = row.avgProgress || 0;
  return (
    <div className="flex items-stretch border-b border-zinc-900/80 group hover:bg-zinc-900/30" style={{ height: rowH }} data-testid={testid}>
      <div className="w-36 shrink-0 flex items-center gap-2 px-3 text-[11px]">
        <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: row.color }}/>
        <span className={`truncate ${row.empty ? 'text-zinc-600' : 'text-zinc-300 group-hover:text-white'}`}>{row.label}</span>
        {!row.empty && <span className="ml-auto text-[9px] text-zinc-500 font-mono">{row.count}</span>}
      </div>
      <div className="flex-1 relative">
        {!row.empty && (
          <div className="absolute top-1 bottom-1 rounded overflow-hidden cursor-default shadow-sm"
               style={{
                 left: `${row.left}%`,
                 width: `${row.width}%`,
                 background: `linear-gradient(90deg, ${row.color}33, ${row.color}99)`,
                 borderLeft: `3px solid ${row.color}`,
               }}
               title={`${row.label} · ${row.count} tasks · ${progress}%`}>
            {/* Fill overlay for progress */}
            <div className="absolute inset-y-0 left-0 bg-white/10" style={{ width: `${progress}%` }}/>
            <div className="relative h-full flex items-center justify-between px-2 text-[10px] font-bold text-white">
              <span className="truncate">{row.count} tasks · {progress}%</span>
              <StatusPips counts={row.statusCounts}/>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPips({ counts }) {
  const pips = [
    { label:'Done',     n: counts.done,        color:'#10b981' },
    { label:'Review',   n: counts.review,      color:'#60a5fa' },
    { label:'Live',     n: counts.in_progress, color:'#f59e0b' },
    { label:'Idle',     n: counts.not_started, color:'#a1a1aa' },
  ].filter(p => p.n > 0);
  if (pips.length === 0) return null;
  return (
    <span className="flex items-center gap-0.5 shrink-0">
      {pips.map((p,i)=>(
        <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} title={`${p.label}: ${p.n}`}/>
      ))}
    </span>
  );
}

function Legend() {
  return (
    <div className="hidden md:flex items-center gap-3 text-[10px] text-zinc-500">
      <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400"/>Done</span>
      <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-blue-400"/>Review</span>
      <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-400"/>In Progress</span>
      <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-zinc-500"/>Not Started</span>
    </div>
  );
}
